import { useState, useCallback, useRef } from 'react';

interface RateLimiterConfig {
  maxAttempts: number;
  windowMs: number;
  onLimitExceeded?: () => void;
}

/**
 * Hook pour limiter le nombre de tentatives d'action dans une fenêtre de temps
 * Protection contre les attaques par force brute
 */
export const useRateLimiter = ({ 
  maxAttempts, 
  windowMs,
  onLimitExceeded 
}: RateLimiterConfig) => {
  const [attempts, setAttempts] = useState<number[]>([]);
  const [isBlocked, setIsBlocked] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const checkLimit = useCallback((): boolean => {
    const now = Date.now();
    // Filtrer les tentatives dans la fenêtre de temps
    const recentAttempts = attempts.filter(time => now - time < windowMs);

    if (recentAttempts.length >= maxAttempts) {
      setIsBlocked(true);
      onLimitExceeded?.();
      
      // Débloquer après la fin de la fenêtre
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setIsBlocked(false);
        setAttempts([]);
      }, windowMs);

      return false;
    }

    // Ajouter la nouvelle tentative
    setAttempts([...recentAttempts, now]);
    return true;
  }, [attempts, maxAttempts, windowMs, onLimitExceeded]);

  const reset = useCallback(() => {
    setAttempts([]);
    setIsBlocked(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  return { checkLimit, isBlocked, reset };
};
